/**
 * StatusBar Component - Persistent footer with edge compute + AI/ML engine info
 */

import { useState } from 'react'
import type { EdgeStatus } from '../../types'

interface StatusBarProps {
  edgeStatus: EdgeStatus | null
}

export function StatusBar({ edgeStatus }: StatusBarProps) {
  const [showEngineInfo, setShowEngineInfo] = useState(false)
  const predictions = edgeStatus?.edge_predictions_count ?? 0
  const syncPending = edgeStatus?.sync_pending ?? 0
  const connectivity = edgeStatus?.connectivity ?? 'FULL'

  return (
    <>
      {/* Expandable AI/ML Engine Info Panel */}
      {showEngineInfo && (
        <div className="border-t border-term-border bg-term-bg px-4 py-3 text-xs">
          <div className="grid grid-cols-3 gap-4">
            {/* Column 1: Model Architecture */}
            <div>
              <div className="text-term-accent font-bold mb-1.5 tracking-wider text-[10px]">5-MODEL ENSEMBLE (41MB EDGE)</div>
              <div className="space-y-0.5 text-term-textDim">
                <div><span className="text-term-text">TRM-C2</span> <span className="text-term-border">30MB</span> — Temporal Recursive, 12-step attention</div>
                <div><span className="text-term-text">XGBoost-C2</span> <span className="text-term-border">5MB</span> — Operational ratios + lag features</div>
                <div><span className="text-term-text">Prophet-C2</span> <span className="text-term-border">2MB</span> — ATO cycle decomposition</div>
                <div><span className="text-term-text">MC-Rate Pred</span> <span className="text-term-border">3MB</span> — Readiness decay forecasting</div>
                <div><span className="text-term-text">Tempo Classifier</span> <span className="text-term-border">1MB</span> — 6-class tempo MLP</div>
              </div>
            </div>

            {/* Column 2: Domain Adaptation */}
            <div>
              <div className="text-term-accent font-bold mb-1.5 tracking-wider text-[10px]">DOMAIN-ADAPTED 20-VAR SCHEMA</div>
              <div className="space-y-0.5 text-term-textDim">
                <div>Same architecture predicts across all 5 domains:</div>
                <div><span className="text-blue-400">AIR</span> — Sortie capacity, MC rate forecast</div>
                <div><span className="text-purple-400">SPACE</span> — Constellation health, coverage gaps</div>
                <div><span className="text-cyan-400">CYBER</span> — Team readiness, rotation needs</div>
                <div><span className="text-amber-400">GROUND</span> — Defense posture, force protection</div>
                <div><span className="text-sky-400">SEA</span> — Air wing generation, tanker demand</div>
              </div>
            </div>

            {/* Column 3: Edge Value Proposition */}
            <div>
              <div className="text-term-accent font-bold mb-1.5 tracking-wider text-[10px]">EDGE COMPUTE VALUE</div>
              <div className="space-y-0.5 text-term-textDim">
                <div><span className="text-term-success">FULL</span> — 5 models + W4M 3-LLM intel (~500MB)</div>
                <div><span className="text-term-warning">DEGRADED</span> — TRM-C2 only, skip LLM pipeline</div>
                <div><span className="text-red-400">DENIED</span> — Edge models only (41MB, any CPU)</div>
                <div className="mt-1 text-term-text">Transfer learning from financial TRM (R² 0.8647)</div>
                <div>→ Same math: FCF = OCF − CapEx ≡ Capacity = Avail − Mx</div>
                <div>→ Edge predictions within 3.2% of full cloud ensemble</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Status Bar */}
      <footer className="border-t border-term-border bg-term-bg px-4 py-2 flex items-center justify-between text-sm shrink-0">
        {/* Left: Edge compute badge + AI engine summary */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                connectivity === 'FULL' ? 'bg-term-success' : 'bg-term-warning'
              } animate-pulse`}
            />
            <span className={`text-xs font-medium ${
              connectivity === 'FULL' ? 'text-term-success' : 'text-term-warning'
            }`}>
              {connectivity === 'FULL' ? 'CLOUD + EDGE' : 'EDGE ONLY'}
            </span>
          </div>
          <span className="text-term-border">|</span>
          <button
            onClick={() => setShowEngineInfo(!showEngineInfo)}
            className="text-term-textDim text-xs hover:text-term-accent transition-colors cursor-pointer"
            title="Click to toggle AI/ML engine details"
          >
            {showEngineInfo ? '▾' : '▸'} 5-MODEL ENSEMBLE · 41MB · 5 DOMAINS · 28 ASSETS
          </button>
          <span className="text-term-border">|</span>
          <span className="text-term-textDim text-xs">
            {predictions} PREDICTIONS
          </span>
          {syncPending > 0 && (
            <>
              <span className="text-term-border">|</span>
              <span className="text-term-warning text-xs">
                {syncPending} SYNC PENDING
              </span>
            </>
          )}
        </div>

        {/* Right: Keyboard shortcuts hint */}
        <div className="flex items-center gap-4 text-term-textDim text-xs">
          <span>[1-9] Tabs</span>
          <span>[Esc] Cancel</span>
        </div>
      </footer>
    </>
  )
}
